import type {
  AssignmentPlan,
  AuthResponse,
  BreakoutStage,
  DevAuthResponse,
  EventContext,
  HoogahAuth,
  LiteContext,
  ParticipantSyncResult,
  ZoomParticipantInput,
} from "./types.js";

export const BASE_URL = import.meta.env.VITE_HOOGAH_API_BASE_URL ?? "https://app.hoogah.co";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Thrown when a token refresh attempt fails (access expired + refresh rejected). */
export class AuthExpiredError extends Error {
  constructor() {
    super("Session expired — re-authentication required");
    this.name = "AuthExpiredError";
  }
}

/**
 * AC2 — no diagnostics in production builds, unless a build explicitly opts in
 * via VITE_ZOOM_DEBUG. Only the staging workflows set that flag; the prod
 * workflow leaves it unset, so prod stays silent.
 */
function debugEnabled(): boolean {
  return !import.meta.env.PROD || Boolean(import.meta.env.VITE_ZOOM_DEBUG);
}

let debugToggle: HTMLButtonElement | null = null;
let debugBody: HTMLElement | null = null;
let debugCount = 0;
// Starts collapsed — a full-height panel sat on top of the real UI and made
// the Assign/Open Rooms buttons unclickable. Collapsed, it's a slim clickable
// strip; tap it to expand and read the log, tap again to get the buttons back.
let debugCollapsed = true;

function setDebugCollapsed(collapsed: boolean): void {
  debugCollapsed = collapsed;
  if (debugBody) debugBody.style.display = collapsed ? "none" : "block";
  if (debugToggle) debugToggle.textContent = `${collapsed ? "▲" : "▼"} Debug (${debugCount})`;
}

/**
 * Appends a line to an on-screen log panel. Lives on <body>, outside #app, so
 * it survives the innerHTML re-renders that drive the normal UI. Pure DOM — it
 * still works when CSP or CORS blocks the debug POST below.
 */
function renderDebugLine(label: string, data: Record<string, unknown>): void {
  if (typeof document === "undefined" || !document.body) return;
  if (!debugToggle || !debugBody) {
    const panel = document.createElement("div");
    panel.id = "zoom-debug-panel";
    panel.setAttribute("style", "position:fixed;left:0;right:0;bottom:0;z-index:2147483647;font:11px/1.35 monospace;");

    debugToggle = document.createElement("button");
    debugToggle.type = "button";
    debugToggle.setAttribute(
      "style",
      "display:block;width:100%;padding:4px 8px;margin:0;border:none;border-top:1px solid #0f0;" +
        "background:#000;color:#0f0;font:11px/1.35 monospace;text-align:left;cursor:pointer;",
    );
    debugToggle.addEventListener("click", () => setDebugCollapsed(!debugCollapsed));

    debugBody = document.createElement("pre");
    debugBody.setAttribute(
      "style",
      "margin:0;padding:4px;height:34vh;overflow:auto;background:#000;color:#0f0;" +
        "white-space:pre-wrap;word-break:break-all;",
    );

    panel.appendChild(debugToggle);
    panel.appendChild(debugBody);
    document.body.appendChild(panel);
    setDebugCollapsed(debugCollapsed);
  }
  const line = document.createElement("div");
  line.textContent = `${new Date().toTimeString().slice(0, 8)} ${label} ${JSON.stringify(data)}`;
  debugBody.appendChild(line);
  debugBody.scrollTop = debugBody.scrollHeight;
  debugCount += 1;
  if (debugCollapsed) debugToggle.textContent = `▲ Debug (${debugCount})`;
}

export async function debugLog(label: string, data: Record<string, unknown>): Promise<void> {
  if (!debugEnabled()) return;
  renderDebugLine(label, data);
  if (import.meta.env.VITE_ZOOM_DEBUG_LOCAL_ONLY) return;
  try {
    await fetch(`${BASE_URL}/api/virtual-events/debug-log/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label, data }),
    });
  } catch {
    // ignore — debug logger never blocks
  }
}

// ---------------------------------------------------------------------------
// Core fetch helper (single attempt — no refresh logic)
// ---------------------------------------------------------------------------

async function request<T>(
  path: string,
  options: RequestInit & { token?: string } = {},
): Promise<T> {
  const { token, ...fetchOptions } = options;
  const headers = new Headers(fetchOptions.headers);
  headers.set("Content-Type", "application/json");
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(response.status, `HTTP ${response.status}: ${body}`);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Refresh endpoint
// ---------------------------------------------------------------------------

interface RefreshResponse {
  access_token: string;
  access_expires_at: string;
  refresh_token: string;
  refresh_expires_at: string;
}

async function callRefreshEndpoint(refreshToken: string): Promise<RefreshResponse> {
  return request<RefreshResponse>("/api/virtual-events/zoom/auth/refresh/", {
    method: "POST",
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
}

// ---------------------------------------------------------------------------
// Authenticated request with automatic 401→refresh→retry
// ---------------------------------------------------------------------------

/**
 * Make an authenticated API request. On 401, attempts one refresh using the
 * stored refresh token and retries. If the refresh also fails, throws
 * AuthExpiredError so the caller can trigger re-authentication.
 *
 * @param auth         Current auth state (access + refresh tokens).
 * @param onRefreshed  Callback invoked with the new access token when a
 *                     refresh succeeds. The caller should persist the update.
 */
export async function authenticatedRequest<T>(
  path: string,
  auth: HoogahAuth,
  onRefreshed: (
    newAccess: string,
    newAccessExpiresAt: string,
    newRefresh: string,
    newRefreshExpiresAt: string,
  ) => void,
  options: Omit<RequestInit, "headers"> = {},
): Promise<T> {
  try {
    return await request<T>(path, { ...options, token: auth.access });
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err;

    // Access token rejected — attempt refresh
    let refreshed: RefreshResponse;
    try {
      refreshed = await callRefreshEndpoint(auth.refresh);
    } catch {
      throw new AuthExpiredError();
    }

    onRefreshed(
      refreshed.access_token,
      refreshed.access_expires_at,
      refreshed.refresh_token,
      refreshed.refresh_expires_at,
    );

    // Retry the original request with the new access token
    return request<T>(path, { ...options, token: refreshed.access_token });
  }
}

// ---------------------------------------------------------------------------
// Public API functions
// ---------------------------------------------------------------------------

export async function devAuth(email: string, eventId?: number): Promise<DevAuthResponse> {
  return request<DevAuthResponse>("/api/virtual-events/zoom/dev-auth/", {
    method: "POST",
    body: JSON.stringify({ email, event_id: eventId }),
  });
}

export async function exchangeContextToken(
  contextToken: string,
  oauthCode: string,
  codeVerifier: string = "",
  zoomMeetingId: string = "",
): Promise<AuthResponse> {
  return request<AuthResponse>("/api/virtual-events/zoom/auth/", {
    method: "POST",
    body: JSON.stringify({
      context_token: contextToken,
      oauth_code: oauthCode,
      code_verifier: codeVerifier,
      zoom_meeting_id: zoomMeetingId,
    }),
  });
}

export async function fetchEventContext(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  meetingUuid: string,
  eventId?: number | null,
): Promise<EventContext> {
  const params = eventId
    ? `event_id=${eventId}`
    : `meeting_uuid=${encodeURIComponent(meetingUuid)}`;
  return authenticatedRequest<EventContext>(
    `/api/virtual-events/zoom/context/?${params}`,
    auth,
    onRefreshed,
  );
}

export async function syncParticipants(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  eventId: number,
  participants: ZoomParticipantInput[],
): Promise<ParticipantSyncResult> {
  return authenticatedRequest<ParticipantSyncResult>(
    `/api/virtual-events/${eventId}/zoom/participants/sync/`,
    auth,
    onRefreshed,
    { method: "POST", body: JSON.stringify({ participants }) },
  );
}

export async function fetchAssignmentPlan(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  eventId: number,
  roundId: number,
): Promise<AssignmentPlan> {
  return authenticatedRequest<AssignmentPlan>(
    `/api/virtual-events/${eventId}/rounds/${roundId}/zoom-assignment-plan/`,
    auth,
    onRefreshed,
  );
}

/** Generates a round's pairs from the sheet — same admin_app.PairingService call the dashboard's "Create Pairs" button makes. */
export async function createRoundPairs(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  eventId: number,
  roundId: number,
): Promise<{ message: string }> {
  return authenticatedRequest<{ message: string }>(
    `/api/virtual-events/${eventId}/rounds/${roundId}/pair/`,
    auth,
    onRefreshed,
    { method: "POST" },
  );
}

export interface RoomAssignmentResult {
  user_id?: number;
  pairing_id?: number;
  email?: string;
  zoom_room_name?: string;
  zoom_participant_id?: string;
  assignment_status?: "assigned" | "failed" | "skipped";
  error_message?: string;
  /** VE-018 AC-3/AC-4: how this placement was determined. */
  matched_by?:
    | "zoom_session"
    | "email"
    | "name"
    | "normalized_name"
    | "partial_name"
    | "alias"
    | "llm_fuzzy"
    | "manual"
    | "";
}

export interface AssignmentRunUpdate {
  status?: "pending" | "in_progress" | "applied" | "partial" | "failed" | "rolled_back";
  stage?: BreakoutStage;
  assigned_participant_count?: number;
  unassigned_participant_count?: number;
  error_summary?: string;
  raw_result_json?: Record<string, unknown>;
  room_assignments?: RoomAssignmentResult[];
}

export async function updateAssignmentRun(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  eventId: number,
  assignmentRunId: number,
  payload: AssignmentRunUpdate,
): Promise<void> {
  await authenticatedRequest<unknown>(
    `/api/virtual-events/${eventId}/assignment-runs/${assignmentRunId}/`,
    auth,
    onRefreshed,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

/** Full-event counterpart of completeLiteRound (VE-027). */
export async function completeAssignmentRound(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  eventId: number,
  roundId: number,
): Promise<{ next_round_id: number | null; already_complete: boolean }> {
  return authenticatedRequest<{ next_round_id: number | null; already_complete: boolean }>(
    `/api/virtual-events/${eventId}/rounds/${roundId}/complete/`,
    auth,
    onRefreshed,
    { method: "POST" },
  );
}

/** Full-event counterpart of fetchLiteMeetingIdentities — same shared,
 * meeting-scoped confirmations, stored against event_id instead of a plan. */
export async function fetchMeetingIdentities(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  eventId: number,
  zoomMeetingUuid: string,
): Promise<LiteMeetingIdentity[]> {
  const response = await authenticatedRequest<{ identities: LiteMeetingIdentity[] }>(
    `/api/virtual-events/${eventId}/zoom/meeting-identities/?zoom_meeting_uuid=${encodeURIComponent(zoomMeetingUuid)}`,
    auth,
    onRefreshed,
  );
  return response.identities ?? [];
}

export async function saveMeetingIdentity(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  eventId: number,
  zoomMeetingUuid: string,
  identity: LiteMeetingIdentity,
): Promise<void> {
  await authenticatedRequest<unknown>(
    `/api/virtual-events/${eventId}/zoom/meeting-identities/`,
    auth,
    onRefreshed,
    { method: "POST", body: JSON.stringify({ zoom_meeting_uuid: zoomMeetingUuid, ...identity }) },
  );
}

/** VE-017 AC-1: save a manually-confirmed Zoom name against an attendee's record. */
export async function saveZoomNameAlias(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  eventId: number,
  userId: number,
  displayName: string,
): Promise<void> {
  await authenticatedRequest<unknown>(
    `/api/virtual-events/${eventId}/zoom/name-aliases/`,
    auth,
    onRefreshed,
    { method: "POST", body: JSON.stringify({ user_id: userId, display_name: displayName }) },
  );
}

/** Response from fuzzy match endpoint. */
export interface FuzzyMatchResponse {
  matches: Array<{
    live_uuid: string;
    attendee_key: string;
    confidence: number;
    reasoning: string;
  }>;
  cached: boolean;
  llm_used: boolean;
  fallback_reason: string | null;
}

/** Fetch LLM-based fuzzy name matches for live Zoom participants. */
export async function fetchFuzzyMatches(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  meetingUuid: string,
  liveParticipants: Array<{ uuid: string; screen_name: string }>,
  planParticipants: Array<{ attendee_key: string; display_name: string; email: string }>,
  eventId?: number,
): Promise<FuzzyMatchResponse> {
  const url = eventId
    ? `/api/virtual-events/${eventId}/zoom/fuzzy-match/`
    : "/api/lite-portal/zoom/fuzzy-match/";

  return authenticatedRequest<FuzzyMatchResponse>(
    url,
    auth,
    onRefreshed,
    {
      method: "POST",
      body: JSON.stringify({
        meeting_uuid: meetingUuid,
        live_participants: liveParticipants,
        plan_participants: planParticipants,
      }),
    },
  );
}

// ---------------------------------------------------------------------------
// Lite (standalone) endpoints
// ---------------------------------------------------------------------------

export async function fetchLiteContext(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
): Promise<LiteContext> {
  return authenticatedRequest<LiteContext>("/api/lite-portal/zoom/context/", auth, onRefreshed);
}


export async function fetchLiteAssignmentPlan(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  roundId: number,
): Promise<AssignmentPlan> {
  return authenticatedRequest<AssignmentPlan>(
    `/api/lite-portal/zoom/rounds/${roundId}/assignment-plan/`,
    auth,
    onRefreshed,
  );
}

/** One organiser-confirmed "this plan row is that person", shared meeting-wide. */
export interface LiteMeetingIdentity {
  attendee_key: string;
  zoom_participant_id: string;
  screen_name: string;
}

export async function fetchLiteMeetingIdentities(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  zoomMeetingUuid: string,
): Promise<LiteMeetingIdentity[]> {
  const response = await authenticatedRequest<{ identities: LiteMeetingIdentity[] }>(
    `/api/lite-portal/zoom/meeting-identities/?zoom_meeting_uuid=${encodeURIComponent(zoomMeetingUuid)}`,
    auth,
    onRefreshed,
  );
  return response.identities ?? [];
}

export async function saveLiteMeetingIdentity(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  zoomMeetingUuid: string,
  identity: LiteMeetingIdentity,
): Promise<void> {
  await authenticatedRequest<unknown>("/api/lite-portal/zoom/meeting-identities/", auth, onRefreshed, {
    method: "POST",
    body: JSON.stringify({ zoom_meeting_uuid: zoomMeetingUuid, ...identity }),
  });
}

export async function updateLiteAssignmentRun(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  assignmentRunId: number,
  payload: AssignmentRunUpdate,
): Promise<void> {
  await authenticatedRequest<unknown>(
    `/api/lite-portal/zoom/assignment-runs/${assignmentRunId}/`,
    auth,
    onRefreshed,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

export async function completeLiteRound(
  auth: HoogahAuth,
  onRefreshed: (newAccess: string, newAccessExpiresAt: string, newRefresh: string, newRefreshExpiresAt: string) => void,
  roundId: number,
  zoomMeetingId: string,
  zoomMeetingUuid: string,
): Promise<{ next_round_id: number | null }> {
  return authenticatedRequest<{ next_round_id: number | null }>(
    `/api/lite-portal/zoom/rounds/${roundId}/complete/`,
    auth,
    onRefreshed,
    {
      method: "POST",
      body: JSON.stringify({ zoom_meeting_id: zoomMeetingId, zoom_meeting_uuid: zoomMeetingUuid }),
    },
  );
}
